/**
 * The badge and the filter chip a tenant document gets, from its
 * `tenant_documents.document_type`.
 *
 * Kept in its own module rather than inside the Documents tab for the same
 * reason as tenant-audit-titles.ts: the set of labels can then be checked
 * against the set of document types the app actually WRITES (see
 * tenant-document-labels.test.ts). A type filed by code but missing here is
 * the regression this guards — it lands on the "Document" fallback, and a
 * handover-protocol PDF ends up labelled the same as any stray upload.
 *
 * Note the two maps cover more types than `DOC_TYPE_OPTIONS`, the dropdown an
 * admin uploads through. Handover protocols, the deposit statement and the
 * Wohnungsgeberbestätigung are generated and filed by code — they are never
 * picked from that list, which is exactly why they were missing.
 */

/**
 * Move-in and move-out get separate badges rather than one shared "Handover
 * protocol": which of the two a PDF documents is the whole point of reading it.
 */
export const DOC_TYPE_LABELS: Record<string, string> = {
  id_passport: "ID / Passport",
  residence_permit: "Residence permit",
  proof_of_income: "Proof of income",
  employment_letter: "Employment letter",
  reference: "Reference",
  contract: "Contract",
  move_in_protocol: "Move-in protocol",
  move_out_protocol: "Move-out protocol",
  deposit_statement: "Deposit statement",
  wohnungsgeberbestaetigung: "Wohnungsgeberbestätigung",
  other: "Other",
};

/**
 * Which filter chip a type falls under. Only types that belong somewhere other
 * than the "Other" catch-all appear here.
 *
 * Protocols go to Handover — the chip already exists for the handover
 * attachments on admin notes, and a protocol PDF is the document it names.
 * Filing it under "Other", next to a stray screenshot, made that filter lie.
 */
export const DOC_TYPE_GROUPS: Record<string, string> = {
  id_passport: "ID",
  residence_permit: "ID",
  proof_of_income: "Income",
  employment_letter: "Income",
  reference: "Reference",
  contract: "Contract",
  move_in_protocol: "Handover",
  move_out_protocol: "Handover",
};

/**
 * `slug -> display label` for labels an admin created via "+ New label"
 * (`tenant_document_labels` rows). Passed in by callers that have fetched
 * them — this module has no DB access of its own.
 */
export type CustomDocLabels = Record<string, string>;

/**
 * `translate` is the app's `t(key, fallback)`. The built-in names are English
 * here; when it is passed, each is looked up as `admin.docType.<type>` with this
 * English as the fallback, so a German admin sees a German name wherever a row
 * exists (COL-618 seeds `residence_permit`) and today's English everywhere else.
 * Custom labels are admin-typed text and are never translated.
 */
export function labelForDocType(
  type: string | null | undefined,
  customLabels?: CustomDocLabels,
  translate?: (key: string, fallback: string) => string
): string {
  const key = type ?? "other";
  const builtIn = DOC_TYPE_LABELS[key];
  if (builtIn)
    return translate ? translate(`admin.docType.${key}`, builtIn) : builtIn;
  return (type ? customLabels?.[type] : undefined) ?? "Document";
}

/** Custom labels have no chip of their own yet — they fall under "Other". */
export function docGroup(type: string | null | undefined): string {
  return DOC_TYPE_GROUPS[type ?? ""] ?? "Other";
}
